import { z } from "@reflag/shared/utils/zod";

import { APIResponse, BulkDeleteResponse } from "./api";
import { AttributeField, AttributeType } from "./attributeFilter";
import { EntityAttributesSchema } from "./entityAttributes";
import { EnvironmentSelectionQuerySchema } from "./environmentAPI";
import {
  FrequencyNumber,
  FunnelStep,
  SortType,
  sortTypeSchema,
} from "./featureAPI";
import { UIFilterSchema } from "./filter";
import {
  CompanyIdSchema,
  EntityFlagsResponse,
  UpdateEntityFlagsBody,
  UpdateEntityFlagsResponse,
} from "./flagAPI";
import { booleanish } from "./schemas/booleanish";
import { PaginationQueryBaseSchema } from "./schemas/dataTableSchema";
import { ExternalIdSchema } from "./schemas/external";
import { SatisfactionScore } from "./schemas/satisfactionScore";
import { ColumnState } from "./types/columns";
import { Paginated } from "./types/Paginated";

export type FeatureMetric = {
  firstUsed: string | null;
  lastUsed: string | null;
  firstExposureAt: string | null;
  lastExposureAt: string | null;
  exposureCount: number;
  usageCount: number;
};

export type CompanyName = {
  id: string;
  name: string | null;
  avatarUrl: string | null;
  isUnknown?: boolean;
};

export type CompanyListItem = CompanyName & {
  firstSeen: string | null;
  lastSeen: string | null;
  userCount: number;
  eventCount: number;
  feedbackCount: number;
  attributes: Record<string, string>;
  featureMetrics: Record<string, FeatureMetric>;
};

export type CompanyList = Paginated<
  CompanyListItem,
  CompaniesSortBy,
  { attributeTypes: Record<string, AttributeType> }
>;
export type CompanySearchPage = Omit<CompanyList, "totalCount">;
export type CompanyCountResponse = {
  totalCount: number | null;
};

export type CompanyDetail = CompanyListItem; // TODO: support for attribute types

export const CompanyAttributesLiteral = z.custom<`attributes.${string}`>(
  (val) => {
    return (
      typeof val === "string" &&
      val.length <= 255 && // limit as a sanity check to prevent potential abuse
      val.startsWith("attributes.")
    );
  },
);

export const CompanyFeatureMetricLiteral = <K extends string>(key: K) =>
  z.custom<`featureMetrics.${string}.${K}`>((val) => {
    return (
      typeof val === "string" &&
      val.length <= 255 && // limit as a sanity check to prevent potential abuse
      RegExp(`^featureMetrics\\.\\w+\\.${key}$`).test(val)
    );
  });

export const CompaniesCoreSortByColumns = [
  "name",
  "id",
  "firstSeen",
  "lastSeen",
  "feedbackCount",
  "userCount",
] as const;

export const CompaniesCoreSortBySchema = z
  .enum(CompaniesCoreSortByColumns)
  .or(CompanyAttributesLiteral);

export const CompaniesSortBySchema = CompaniesCoreSortBySchema.or(
  CompanyFeatureMetricLiteral("funnelStep"),
)
  .or(CompanyFeatureMetricLiteral("satisfaction"))
  .or(CompanyFeatureMetricLiteral("firstUsed"))
  .or(CompanyFeatureMetricLiteral("lastUsed"))
  .or(CompanyFeatureMetricLiteral("firstExposureAt"))
  .or(CompanyFeatureMetricLiteral("lastExposureAt"))
  .or(CompanyFeatureMetricLiteral("frequency"))
  .or(CompanyFeatureMetricLiteral("eventCount"))
  .or(CompanyFeatureMetricLiteral("exposureCount"))
  .or(CompanyFeatureMetricLiteral("usageCount"));

export type CompaniesSortBy = z.infer<typeof CompaniesSortBySchema>;

export const CompaniesColumnSchema = CompaniesSortBySchema; // All columns are sortable
export type CompaniesColumn = z.infer<typeof CompaniesColumnSchema>;

export type CompaniesColumnState = ColumnState<CompaniesColumn>;

export type EnvironmentSelectionQueryType = z.input<
  typeof EnvironmentSelectionQuerySchema
>;

export const CompaniesQuerySchema = z
  .object({
    sortBy: CompaniesSortBySchema.default("name").describe(
      "Column to sort companies by",
    ),
    filter: UIFilterSchema.optional().describe(
      "Filter companies using advanced criteria",
    ),
    idNameFilter: z
      .string()
      .optional()
      .describe("Filter companies by id or name"),
    includeDetailsForFeatureIds: z
      .array(z.string())
      .optional()
      .describe("Include details for specified feature ids"),
    includeUserCount: z
      .boolean()
      .optional()
      .default(true)
      .describe("Include user count"),
    includeAllAttributes: z
      .boolean()
      .optional()
      .default(false)
      .describe("Attributes to include"),
    includeAttributes: z
      .array(z.string())
      .optional()
      .default([])
      .describe("Attributes to include"),
    columns: z.array(z.string()).optional().describe("Columns to include"),
  })
  .extend(PaginationQueryBaseSchema().shape)
  .strict();

export type CompaniesSearchBody = z.input<typeof CompaniesQuerySchema>;

export const CompaniesCountBodySchema = CompaniesQuerySchema.pick({
  filter: true,
  idNameFilter: true,
});

export type CompaniesCountBody = z.input<typeof CompaniesCountBodySchema>;

export const CompaniesExportQuerySchema = CompaniesQuerySchema.omit({
  pageIndex: true,
  pageSize: true,
});

export type CompaniesExportBody = z.input<typeof CompaniesExportQuerySchema>;

export const AttributeValuesQuerySchema = z.object({
  filter: z.string().optional().default(""),
  limit: z.preprocess(
    (a) => parseInt(z.string().optional().default("10").parse(a), 10),
    z.number().max(20),
  ),
});

/* Company Features */

export type CompanyFeatureDTO = {
  id: string;
  key: string;
  parentFeatureId: string | null;
  version: string;
  name: string;
  icon: string | null;
  iconColor: string | null;
  source: "event" | "attribute";
  isArchived: boolean;
  isPermanent: boolean;
  isStale: boolean;
  createdAt: string;
  funnelStep: FunnelStep | null;
  firstUsed: string | null;
  lastUsed: string | null;

  feedbackCount: number;
  usagesPerDay: { date: string; count: number }[];
  exposuresPerDay: { date: string; count: number }[];
  exposureCount: number | null;
  usageCount: number | null;

  isTargeted: boolean;
  isExplicitlyTargeted: boolean;

  // legacy
  eventCount: number | null;
  frequency: FrequencyNumber | null;
  satisfaction: SatisfactionScore | null;

  // exposures
  firstPositiveCheckTime: string | null;
  lastPositiveCheckTime: string | null;
  lastCheckTime: string | null;
  lastCheckResult: boolean | null;
};

export const CompanyFeaturesSortByColumns = [
  "name",
  "isTargeted",
  "lastCheckTime",
  "createdAt",
  "satisfaction",
  "feedbackCount",
  "funnelStep",
  "firstUsed",
  "lastUsed",
  "frequency",
  "eventCount",

  "firstPositiveCheckTime",
  "lastPositiveCheckTime",

  "exposureCount",
  "usageCount",
] as const;

export const CompanyFeaturesSortBySchema = z.enum(CompanyFeaturesSortByColumns);
export type CompanyFeaturesSortBy = z.infer<typeof CompanyFeaturesSortBySchema>;

export const CompanyFeaturesColumns = [
  ...CompanyFeaturesSortByColumns,
  "exposuresPerDay",
  "usagesPerDay",
] as const;
export const CompanyFeaturesColumnSchema = z.enum(CompanyFeaturesColumns);
export type CompanyFeaturesColumn = z.infer<typeof CompanyFeaturesColumnSchema>;

export const CompanyFeaturesQuerySchema = z
  .object({
    viewId: z.string().optional(),
    sortOrder: z.enum(["asc", "desc"]).optional().default("desc"),
    sortBy: CompanyFeaturesSortBySchema.optional().default("createdAt"),
    sortType: sortTypeSchema.optional().default("flat"),
  })
  .extend(EnvironmentSelectionQuerySchema.shape)
  .strict();

export type CompanyFeaturesQuery = z.input<typeof CompanyFeaturesQuerySchema>;

export type CompanyFeaturesList = Paginated<
  CompanyFeatureDTO,
  CompanyFeaturesSortBy,
  { sortType: SortType }
>;

/* Company Attributes */

export const DeleteEntityAttributeBodySchema = z
  .object({
    entityType: z.enum(["company", "user"]),
    key: z.string().min(1).max(128),
  })
  .strict();

export const CompanyAttributesQuerySchema =
  EnvironmentSelectionQuerySchema.extend({
    filter: z.string().optional(),
  }).strict();

export type CompanyAttributesQueryType = z.input<
  typeof CompanyAttributesQuerySchema
>;

export const CompanyAttributeValuesQuerySchema =
  EnvironmentSelectionQuerySchema.extend(AttributeValuesQuerySchema.shape);

export type CompanyAttributeValuesQueryType = z.input<
  typeof CompanyAttributeValuesQuerySchema
>;

export const BulkCompaniesBodySchema = z
  .object({
    ids: z.array(z.string()).min(1),
  })
  .strict();

export type BulkCompaniesBody = z.input<typeof BulkCompaniesBodySchema>;

export const CompanyDetailQuerySchema = EnvironmentSelectionQuerySchema;

export type CompanyDetailQueryType = z.input<typeof CompanyDetailQuerySchema>;

export const ManageCompanySchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(128)
      .optional()
      .describe(
        "Company name. Omit it to preserve an existing name; new companies may be unnamed.",
      ),
    attributes: EntityAttributesSchema,
  })
  .strict();

export const ManageCompanyParamsSchema = z.object({
  companyId: CompanyIdSchema,
});

export const ManageCompanyResponseSchema = ManageCompanySchema.extend({
  id: CompanyIdSchema,
});

export type ManageCompany = z.input<typeof ManageCompanySchema>;
export type ManageCompanyResponse = z.output<
  typeof ManageCompanyResponseSchema
>;
export const DeleteCompanyBodySchema = z
  .object({
    deleteUsers: booleanish.optional().default(true),
  })
  .optional();
export type DeleteCompanyBodyType = z.input<typeof DeleteCompanyBodySchema>;

export const DeleteCompaniesBodySchema = z
  .object({
    ids: z
      .array(ExternalIdSchema)
      .min(1)
      .transform((ids) => [...new Set(ids)]),
    deleteUsers: booleanish.optional().default(true),
  })
  .strict();

export type DeleteCompaniesBody = z.input<typeof DeleteCompaniesBodySchema>;

// @deprecated -- remove after 6th of april
export const CompanyFeatureBodySchema = z
  .object({
    features: z
      .array(
        z
          .object({
            // Allow either featureKey or featureId, but require at least one
            featureKey: z.string().optional(),
            featureId: z.string().optional(),
            isEnabled: z.boolean(),
          })
          .refine(
            (data) =>
              data.featureKey !== undefined || data.featureId !== undefined,
            {
              message: "Either featureKey or featureId must be provided",
            },
          ),
      )
      .min(1),
    changeDescription: z.string().optional(),
  })
  .strict();

export type CompanyFeatureBody = z.input<typeof CompanyFeatureBodySchema>;

export interface CompanyAPI {
  "/apps/:appId/companies/search": {
    POST: {
      response: APIResponse<CompanySearchPage>;
      params: { appId: string };
      body: CompaniesSearchBody;
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/companies/search/count": {
    POST: {
      response: APIResponse<CompanyCountResponse>;
      params: { appId: string };
      body: CompaniesCountBody;
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/companies/export": {
    POST: {
      response: APIResponse<void>;
      params: { appId: string };
      body: Omit<CompaniesSearchBody, "pageIndex" | "pageSize">;
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/company-names": {
    POST: {
      response: APIResponse<CompanyName[]>;
      params: { appId: string };
      body: BulkCompaniesBody;
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/companies/:companyId": {
    GET: {
      response: APIResponse<{
        company: CompanyDetail;
      }>;
      params: { appId: string; companyId: string };
      query: CompanyDetailQueryType;
    };
  };
  "/apps/:appId/companies/:companyId/features": {
    GET: {
      response: CompanyFeaturesList;
      params: { appId: string; companyId: string };
      query: CompanyFeaturesQuery;
    };
  };
  "/apps/:appId/envs/:envId/attributes": {
    DELETE: {
      response: APIResponse<{ queued: true; operationId: string }>;
      params: { appId: string; envId: string };
      body: z.input<typeof DeleteEntityAttributeBodySchema>;
    };
  };
  "/apps/:appId/company-attributes": {
    GET: {
      response: APIResponse<{
        attributes: AttributeField[];
      }>;
      params: { appId: string };
      query: CompanyAttributesQueryType;
    };
  };
  "/apps/:appId/company-attributes/:attribute/values": {
    GET: {
      response: APIResponse<{
        values: string[];
      }>;
      params: { appId: string; attribute: string };
      query: CompanyAttributeValuesQueryType;
    };
  };
  "/apps/:appId/envs/:envId/companies/:companyId/flags": {
    GET: {
      response: APIResponse<EntityFlagsResponse>;
      params: { appId: string; envId: string; companyId: string };
    };
    PATCH: {
      response: APIResponse<UpdateEntityFlagsResponse>;
      params: { appId: string; envId: string; companyId: string };
      body: UpdateEntityFlagsBody;
    };
  };
  "/apps/:appId/envs/:envId/companies": {
    DELETE: {
      response: BulkDeleteResponse;
      params: { appId: string; envId: string };
      body: DeleteCompaniesBody;
    };
  };
  "/apps/:appId/envs/:envId/companies/:companyId": {
    PUT: {
      response: APIResponse<ManageCompanyResponse>;
      params: { appId: string; envId: string; companyId: string };
      body: ManageCompany;
    };
    DELETE: {
      response: APIResponse<object>;
      params: { appId: string; envId: string; companyId: string };
      body: DeleteCompanyBodyType;
    };
  };
  "/apps/:appId/envs/:envId/companies/:companyId/users/:userId": {
    DELETE: {
      response: APIResponse<object>;
      params: {
        appId: string;
        envId: string;
        companyId: string;
        userId: string;
      };
    };
  };
}
